import { useEffect, useState } from "react";

export interface PixelSize {
    width: number;
    height: number;
}

/**
 * Фактический размер файла, измеренный по самому файлу.
 *
 * Ярлык разрешения (`2K`) — это ОСЬ ЗАКАЗА, по ней считается цена, и у разных
 * платформ под одним ярлыком лежат разные пиксели: у higgsfield `2k` — это
 * 2752×1536, а наш провайдер под тем же ярлыком вернёт свой размер (у Kie
 * ярлык вообще не расшифрован). Поэтому размер не выводится из ярлыка и не
 * берётся из настроек, а меряется у того файла, который человек видит.
 *
 * Меряет браузер: и картинка, и ролик к этому моменту уже отрисованы в
 * сообщении, поэтому запрос уходит в кэш, а не в сеть. Результат делится между
 * вызовами — одна и та же генерация показана и в ленте, и в предпросмотре.
 */
const cache = new Map<string, PixelSize>();

function measure(url: string, kind: "image" | "video"): Promise<PixelSize | null> {
    return new Promise((resolve) => {
        if (kind === "image") {
            const img = new Image();
            img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = () => resolve(null);
            img.src = url;
            return;
        }
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => resolve({ width: video.videoWidth, height: video.videoHeight });
        video.onerror = () => resolve(null);
        video.src = url;
    });
}

/**
 * Размер медиафайла в пикселях, `null` — пока не измерен или измерить нечего.
 *
 * У звука размера нет по природе, поэтому `kind: "audio"` не меряется вовсе;
 * недоступный файл (истёкшая ссылка, ещё не доехавший артефакт) тоже отдаёт
 * `null` — подпись тогда остаётся с одним ярлыком, а не показывает нули.
 */
export function useMediaPixelSize(url: string | null, kind: "image" | "video" | "audio"): PixelSize | null {
    const measurable = url != null && kind !== "audio";
    const [size, setSize] = useState<PixelSize | null>(() => (url != null ? (cache.get(url) ?? null) : null));

    useEffect(() => {
        if (!measurable) {
            setSize(null);
            return;
        }
        const known = cache.get(url);
        if (known) {
            setSize(known);
            return;
        }
        let alive = true;
        void measure(url, kind).then((measured) => {
            if (measured) cache.set(url, measured);
            // Компонент мог размонтироваться, пока файл читался из кэша браузера.
            if (alive) setSize(measured);
        });
        return () => {
            alive = false;
        };
    }, [url, kind, measurable]);

    return measurable ? size : null;
}

/** `2752×1536` — знак умножения, а не буква `x`. */
export function pixelSizeLabel(size: PixelSize): string {
    return `${size.width}×${size.height}`;
}
